<script lang="tsx" setup>
import { useI18n } from 'vue-i18n'
import useThemeVars from '@lib/composables/useThemeVars'
import CDropdown from '@lib/components/CDropdown/CDropdown.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CCheckbox from '@lib/components/CCheckbox/CCheckbox.vue'
import tooltipDirective from '@lib/directives/tooltip'
import { computed } from 'vue'
import type { DropdownMixedOption } from 'naive-ui/es/dropdown/src/interface'

const props = defineProps<{
  selectedCount: number
  count: number
  areAllSelectedFavorites?: boolean
  disabled?: boolean
  showMore?: boolean
  moreActions?: {
    addTags?: boolean
    delete?: boolean
    manageAttributes?: boolean
    manageFileAttributes?: boolean
    removeTags?: boolean
    manageDistribution?: boolean
    manageFileDistributions?: boolean
  }
  showMove?: boolean
  showDelete?: boolean
  showFavorite?: boolean
  showSharebox?: boolean
  shareboxDisabled?: boolean
  shareboxTooltip?: string
  isLoadingEdit?: boolean
  isLoadingMove?: boolean
  isLoadingDelete?: boolean
  isLoadingFavorite?: boolean
}>()

const emit = defineEmits<{
  toggleSelect: [value: boolean]
  toggleFavorite: [value: boolean]
  delete: []
  manageAttributes: []
  manageFileAttributes: []
  addTags: []
  removeTags: []
  move: []
  manageDistribution: []
  manageFileDistributions: []
  addToSharebox: []
}>()

const { t } = useI18n()
const themeVars = useThemeVars()
const vTooltip = tooltipDirective

const areAllSelected = computed(() => props.selectedCount === props.count)
const isNoneSelected = computed(() => props.selectedCount === 0)
const isIndeterminate = computed(() => !areAllSelected.value && !isNoneSelected.value)

function disableActionButton(
  ignoreLoadingProp: 'isLoadingEdit' | 'isLoadingMove' | 'isLoadingDelete' | 'isLoadingFavorite',
) {
  return (
    (props.isLoadingEdit && ignoreLoadingProp !== 'isLoadingEdit') ||
    (props.isLoadingMove && ignoreLoadingProp !== 'isLoadingMove') ||
    (props.isLoadingDelete && ignoreLoadingProp !== 'isLoadingDelete') ||
    (props.isLoadingFavorite && ignoreLoadingProp !== 'isLoadingFavorite')
  )
}

const ACTIONS_FONT_SIZE_CLASS = 'font-size-4'

function getActionsMenu() {
  const actionsMenu: DropdownMixedOption[] = []

  if (props.moreActions?.manageDistribution) {
    actionsMenu.push({
      label: () => (
        <div class={ACTIONS_FONT_SIZE_CLASS}>{t('canvasUI.components.CBulkToolbar.manageDistribution')}</div>
      ),
      key: 'manageDistribution',
      icon: () => <CIcon icon="share-nodes" color={themeVars.value.primary3} />,
    })
  }

  if (props.moreActions?.manageAttributes) {
    actionsMenu.push({
      label: () => <div class={ACTIONS_FONT_SIZE_CLASS}>{t('canvasUI.components.CBulkToolbar.manageAttributes')}</div>,
      key: 'manageAttributes',
      icon: () => <CIcon icon="cog" color={themeVars.value.primary3} />,
    })
  }

  if (props.moreActions?.manageFileDistributions) {
    actionsMenu.push({
      label: () => (
        <div class={ACTIONS_FONT_SIZE_CLASS}>{t('canvasUI.components.CBulkToolbar.manageFileDistributions')}</div>
      ),
      key: 'manageFileDistributions',
      icon: () => <CIcon icon="share-nodes" color={themeVars.value.primary3} />,
    })
  }

  if (props.moreActions?.manageFileAttributes) {
    actionsMenu.push({
      label: () => (
        <div class={ACTIONS_FONT_SIZE_CLASS}>{t('canvasUI.components.CBulkToolbar.manageFileAttributes')}</div>
      ),
      key: 'manageFileAttributes',
      icon: () => <CIcon icon="cog" color={themeVars.value.primary3} />,
    })
  }

  if (props.moreActions?.addTags) {
    actionsMenu.push({
      label: () => <div class={ACTIONS_FONT_SIZE_CLASS}>{t('canvasUI.components.CBulkToolbar.addTags')}</div>,
      key: 'addTags',
      icon: () => <CIcon icon="tags" color={themeVars.value.primary3} />,
    })
  }

  if (props.moreActions?.removeTags) {
    actionsMenu.push({
      label: () => <div class={ACTIONS_FONT_SIZE_CLASS}>{t('canvasUI.components.CBulkToolbar.removeTags')}</div>,
      key: 'removeTags',
      icon: () => <CIcon icon="square-xmark" color={themeVars.value.primary3} />,
    })
  }

  if (props.moreActions?.delete) {
    actionsMenu.push(
      { key: 'd1', type: 'divider' },
      {
        label: () => (
          <div class={ACTIONS_FONT_SIZE_CLASS} style={{ color: themeVars.value.error }}>
            {t('canvasUI.components.CBulkToolbar.delete')}
          </div>
        ),
        key: 'delete',
        icon: () => <CIcon icon="trash" color={themeVars.value.error} />,
      },
    )
  }

  return actionsMenu
}

function handleActions(key: string) {
  switch (key) {
    case 'manageAttributes':
      return emit('manageAttributes')
    case 'manageFileAttributes':
      return emit('manageFileAttributes')
    case 'addTags':
      return emit('addTags')
    case 'removeTags':
      return emit('removeTags')
    case 'delete':
      return emit('delete')
    case 'manageDistribution':
      return emit('manageDistribution')
    case 'manageFileDistributions':
      return emit('manageFileDistributions')
    case 'addToSharebox':
      return emit('addToSharebox')
    default:
      return
  }
}
</script>

<template>
  <div class="c-bulk-toolbar flex items-center pa-4 rounded-2 gap-6 bg-base">
    <div class="flex items-center gap-4">
      <CCheckbox
        v-tooltip="
          areAllSelected
            ? t('canvasUI.components.CBulkToolbar.deselectAll')
            : t('canvasUI.components.CBulkToolbar.selectAll')
        "
        :checked="areAllSelected"
        :disabled="disabled"
        :indeterminate="isIndeterminate"
        :label="t('canvasUI.components.CBulkToolbar.selected', { selectionIndicator: `${selectedCount} / ${count}` })"
        @click.stop="emit('toggleSelect', !areAllSelected)"
      />
      <CButton
        v-if="props.showSharebox"
        v-tooltip="props.shareboxTooltip || t('canvasUI.browserApp.contextMenu.addToSharebox')"
        circle
        :disabled="props.shareboxDisabled"
        secondary
        size="small"
        @click.stop="emit('addToSharebox')"
      >
        <template #icon>
          <CIcon fa-type="fa-kit" icon="regular-share-from-square-circle-plus" size="12px" />
        </template>
      </CButton>
    </div>
    <div class="flex items-center gap-2">
      <slot name="start" />
      <CButton
        v-if="showFavorite"
        v-tooltip="
          areAllSelectedFavorites
            ? t('canvasUI.components.CBulkToolbar.removeFromFavorites')
            : t('canvasUI.components.CBulkToolbar.addToFavorites')
        "
        circle
        :color="areAllSelectedFavorites ? themeVars.accent : themeVars.primary"
        :disabled="disabled || disableActionButton('isLoadingFavorite')"
        :loading="isLoadingFavorite"
        :secondary="!areAllSelectedFavorites"
        size="small"
        @click.stop="emit('toggleFavorite', !areAllSelectedFavorites)"
      >
        <template #icon>
          <CIcon :fa-type="areAllSelectedFavorites ? 'fas' : undefined" icon="heart" size="12px" />
        </template>
      </CButton>
      <CButton
        v-if="showMove"
        v-tooltip="t('canvasUI.components.CBulkToolbar.move')"
        circle
        :disabled="disabled || disableActionButton('isLoadingMove')"
        :loading="isLoadingMove"
        secondary
        size="small"
        @click="emit('move')"
      >
        <template #icon>
          <CIcon icon="folder-arrow-up" />
        </template>
      </CButton>
      <CButton
        v-if="showDelete"
        v-tooltip="t('canvasUI.components.CBulkToolbar.delete')"
        circle
        :disabled="disabled || disableActionButton('isLoadingDelete')"
        :loading="isLoadingDelete"
        secondary
        size="small"
        type="error"
        @click="emit('delete')"
      >
        <template #icon>
          <CIcon icon="trash" />
        </template>
      </CButton>
      <CDropdown v-if="showMore" :options="getActionsMenu()" trigger="click" @select="handleActions">
        <CButton v-tooltip="t('canvasUI.components.CBulkToolbar.moreActions')" circle size="small">
          <template #icon>
            <CIcon icon="ellipsis" size="16px" />
          </template>
        </CButton>
      </CDropdown>
      <slot name="end" />
    </div>
  </div>
</template>
